import {
  transferAchCancelTransfer,
  transferAchClearTransfer,
  transferCheckCancelHeldCheck,
  transferCheckClearHeldCheck,
  transferRealtimeCancelTransfer,
  transferRealtimeClearTransfer,
  transferSwiftCancelHeldTransfer,
  transferSwiftClearHeldTransfer,
  transferWireCancelWireTransfer,
  transferWireClearWireTransfer,
} from '@column/api';

import type { PlatformConfig } from '~/repositories/PlatformRepository';
import type { TransferEntryType } from '~/repositories/Transfer';

interface InternationalWireDetailState {
  hasData: boolean;
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
}

export const isInternationalWireApprovalBlocked = (
  transferType: string | undefined,
  detailState: InternationalWireDetailState | undefined
): boolean =>
  transferType === 'swift' &&
  (!detailState || !detailState.hasData || detailState.isError || detailState.isFetching || detailState.isLoading);

export const isApprovalCandidateStatus = (status: string | undefined): boolean => {
  const normalizedStatus = status?.toLowerCase();
  return normalizedStatus === 'pre_review' || normalizedStatus === 'hold';
};

export const isApprovalHold = (status: string | undefined): boolean => status?.toLowerCase() === 'hold';

export interface ApprovalHoldRequest {
  comment?: string;
  decision: 'approved' | 'rejected';
}

interface SubmitApprovalHoldParams {
  id: string;
  request: ApprovalHoldRequest;
  transferType: TransferEntryType;
}

interface ApprovalHoldResult {
  id: string;
  status: string;
}

export const submitApprovalHold = async ({
  id,
  request,
  transferType,
}: SubmitApprovalHoldParams): Promise<ApprovalHoldResult> => {
  const body = { comment: request.comment };
  const isApproved = request.decision === 'approved';

  switch (transferType) {
    case 'ach_credit':
    case 'ach_debit': {
      const { data } = await (isApproved ? transferAchClearTransfer : transferAchCancelTransfer)({
        path: { ach_transfer_id: id },
        body,
        throwOnError: true,
      });
      return data;
    }
    case 'wire': {
      const { data } = await (isApproved ? transferWireClearWireTransfer : transferWireCancelWireTransfer)({
        path: { wire_transfer_id: id },
        body,
        throwOnError: true,
      });
      return data;
    }
    case 'realtime': {
      const { data } = await (isApproved ? transferRealtimeClearTransfer : transferRealtimeCancelTransfer)({
        path: { realtime_transfer_id: id },
        body,
        throwOnError: true,
      });
      return data;
    }
    case 'swift': {
      const { data } = await (isApproved ? transferSwiftClearHeldTransfer : transferSwiftCancelHeldTransfer)({
        path: { swift_transfer_id: id },
        body,
        throwOnError: true,
      });
      return data;
    }
    case 'check_debit': {
      const { data } = await (isApproved ? transferCheckClearHeldCheck : transferCheckCancelHeldCheck)({
        path: { check_transfer_id: id },
        body,
        throwOnError: true,
      });
      return { id: data.id, status: data.status };
    }
    default:
      throw new Error(`Approval holds are not supported for ${transferType} transfers.`);
  }
};

export const isReviewRequired = (
  type: string,
  transferApprovalConfig: PlatformConfig['transferApprovalConfig'] | undefined,
  enableSwiftTransferApprovals: boolean
): boolean => {
  if (!transferApprovalConfig) return false;

  switch (type) {
    case 'ach_credit':
      return transferApprovalConfig.enabledForAchCredits ?? false;
    case 'ach_debit':
      return transferApprovalConfig.enabledForAchDebits ?? false;
    case 'issue-check':
      return transferApprovalConfig.enabledForCheckDebits ?? false;
    case 'intl-wire':
      return enableSwiftTransferApprovals && (transferApprovalConfig.enabledForInternationalWire ?? false);
    case 'wire':
      return transferApprovalConfig.enabledForWire ?? false;
    case 'realtime':
      return transferApprovalConfig.enabledForRealtime ?? false;
    default:
      return false;
  }
};
